"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { formatProfileName } from "../../../utils/profileName";

function calculateAgeFromDob(value, onDateValue = new Date()) {
  if (!value || !onDateValue) return null;

  const dob = new Date(`${value}T00:00:00`);
  const onDate = onDateValue instanceof Date ? onDateValue : new Date(onDateValue);

  if (Number.isNaN(dob.getTime()) || Number.isNaN(onDate.getTime())) return null;

  let age = onDate.getFullYear() - dob.getFullYear();
  const monthDifference = onDate.getMonth() - dob.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && onDate.getDate() < dob.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function formatRoleplayNhsNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return digits || "Not assigned";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

function formatDate(value) {
  if (!value) return "Not recorded";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Not recorded";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function toDateTimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const pad = (number) => String(number).padStart(2, "0");
  return [
    date.getFullYear(),
    "-",
    pad(date.getMonth() + 1),
    "-",
    pad(date.getDate()),
    "T",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes()),
  ].join("");
}

function textOrFallback(value, fallback = "Not provided") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

export default function AppointmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [patientProfile, setPatientProfile] = useState(null);
  const [patientRecord, setPatientRecord] = useState(null);
  const [patientLoading, setPatientLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [staff, setStaff] = useState([]);
  const [cancelled, setCancelled] = useState(false);

  const loadPatientDetails = useCallback(async (appointmentData) => {
    setPatientLoading(true);
    setPatientProfile(null);
    setPatientRecord(null);

    try {
      let visit = null;
      let portalAccountId = appointmentData?.portal_account_id || null;

      if (appointmentData?.patient_id) {
        const { data: patientData, error: patientError } = await supabase
          .from("patients")
          .select("*")
          .eq("id", appointmentData.patient_id)
          .maybeSingle();

        if (patientError) {
          console.error("Unable to load linked patient record:", patientError);
        } else if (patientData) {
          visit = patientData;
          setPatientRecord(patientData);
          portalAccountId = portalAccountId || patientData.portal_account_id || null;
        }
      }

      let portalAccount = null;

      if (portalAccountId) {
        const { data, error } = await supabase
          .from("patient_portal_accounts")
          .select("*")
          .eq("id", portalAccountId)
          .maybeSingle();

        if (error) {
          console.error("Unable to load patient portal profile:", error);
        } else {
          portalAccount = data || null;
        }
      }

      if (!portalAccount && visit?.roleplay_nhs_number) {
        const { data, error } = await supabase
          .from("patient_portal_accounts")
          .select("*")
          .eq("roleplay_nhs_number", visit.roleplay_nhs_number)
          .limit(1)
          .maybeSingle();

        if (!error) portalAccount = data || null;
      }

      if (!portalAccount && appointmentData?.roblox_username) {
        const { data, error } = await supabase
          .from("patient_portal_accounts")
          .select("*")
          .ilike("roblox_username", appointmentData.roblox_username)
          .limit(1)
          .maybeSingle();

        if (!error) portalAccount = data || null;
      }

      setPatientProfile(portalAccount);
    } catch (error) {
      console.error("Unable to load appointment patient details:", error);
    } finally {
      setPatientLoading(false);
    }
  }, []);

  const fetchAppointment = useCallback(async () => {
    const { data, error } = await supabase
      .from("appointments")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(error);
      return;
    }

    setAppointment(data);
    setCancelled(
      data.status === false ||
        String(data.appointment_status || "").trim().toLowerCase() === "cancelled",
    );

    setForm({
      department: data.department || "",
      clinician: data.clinician || "",
      appointment_datetime: toDateTimeLocalValue(data.appointment_datetime),
      duration_minutes: data.duration_minutes ?? 30,
      location: data.location || "Prince George University Hospital",
      address: data.address || "",
      preparation: data.preparation || "",
      notes: data.notes || "",
    });

    await loadPatientDetails(data);
  }, [id, loadPatientDetails]);

  const fetchStaff = useCallback(async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, name_prefix, discord_id, position, role, suspended")
      .order("full_name", { ascending: true });

    if (error) {
      console.error("Unable to load clinicians:", error);
      setStaff([]);
      return;
    }

    const staffOnly = (data || []).filter((person) => {
      const position = String(person.position || "").trim().toLowerCase();
      const role = String(person.role || "").trim().toLowerCase();

      return (
        position !== "patient" &&
        role !== "patient" &&
        person.suspended !== true
      );
    });

    setStaff(staffOnly);
  }, []);

  useEffect(() => {
    void fetchAppointment();
    void fetchStaff();
  }, [fetchAppointment, fetchStaff]);

  const handleEditToggle = () => {
    if (!appointment) return;

    if (!editing) {
      setForm({
        department: appointment.department || "",
        clinician: appointment.clinician || "",
        appointment_datetime: toDateTimeLocalValue(appointment.appointment_datetime),
        duration_minutes: appointment.duration_minutes ?? 30,
        location: appointment.location || "Prince George University Hospital",
        address: appointment.address || "",
        preparation: appointment.preparation || "",
        notes: appointment.notes || "",
      });
    }

    setEditing((current) => !current);
  };

  const sendPortalMessage = async (subject, body, category = "Appointment") => {
    const portalAccountId = patientProfile?.id || appointment?.portal_account_id || null;
    if (!portalAccountId) return;

    const { data: userData } = await supabase.auth.getUser();
    const { error: messageError } = await supabase.from("patient_messages").insert({
      portal_account_id: portalAccountId,
      patient_id: appointment?.patient_id || null,
      subject,
      body,
      category,
      action_url: "/patient-portal",
      created_by: userData?.user?.id || null,
    });

    if (messageError) {
      console.error("Unable to create patient portal message:", messageError);
    }
  };

  const sendDiscordUpdate = async (discordId, clinicianId) => {
    if (!discordId || !clinicianId) return;

    try {
      const response = await fetch("/api/send-appointment-dm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appointmentId: id,
          patientDiscordId: discordId,
          clinicianProfileId: clinicianId,
        }),
      });

      if (!response.ok) {
        console.error("Failed to send Discord DM:", await response.text());
      }
    } catch (err) {
      console.error("Failed to send Discord DM:", err);
    }
  };

  const handleSave = async () => {
    const dob = patientProfile?.date_of_birth || appointment?.patient_dob || null;
    const recalculatedAge = calculateAgeFromDob(dob, form.appointment_datetime);

    const updatePayload = {
      department: form.department,
      clinician: form.clinician || null,
      appointment_datetime: form.appointment_datetime,
      duration_minutes: Number(form.duration_minutes) || 30,
      location: form.location || "Prince George University Hospital",
      address: form.address || null,
      preparation: form.preparation || null,
      notes: form.notes || null,
      ...(dob ? { patient_dob: dob } : {}),
      ...(recalculatedAge !== null ? { patient_age: recalculatedAge } : {}),
    };

    const { error } = await supabase
      .from("appointments")
      .update(updatePayload)
      .eq("id", id);

    if (error) {
      alert("Error saving changes");
      console.error(error);
      return;
    }

    setEditing(false);
    await fetchAppointment();

    const discordId = patientProfile?.discord_id || appointment?.discord_id || null;
    await sendDiscordUpdate(discordId, form.clinician);
    await sendPortalMessage(
      "Your appointment has been updated",
      `Your ${form.department || "RUHT"} appointment is now scheduled for ${new Date(form.appointment_datetime).toLocaleString("en-GB")} at ${form.location || "RUHT"}.`,
    );
    alert("Changes saved!");
  };

  const handleCancel = async () => {
    const { error } = await supabase
      .from("appointments")
      .update({ status: false, appointment_status: "Cancelled" })
      .eq("id", id);

    if (error) {
      alert("Error cancelling appointment");
      console.error(error);
      return;
    }

    setCancelled(true);
    await fetchAppointment();

    const discordId = patientProfile?.discord_id || appointment?.discord_id || null;
    await sendDiscordUpdate(discordId, appointment?.clinician || form.clinician);
    await sendPortalMessage(
      "Appointment cancelled",
      `Your ${appointment?.department || "RUHT"} appointment scheduled for ${new Date(appointment?.appointment_datetime).toLocaleString("en-GB")} has been cancelled.`,
    );
    alert("Appointment cancelled");
  };

  if (!appointment) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="nhsuk-width-container mt-5">
          <p>Loading appointment…</p>
        </main>
      </div>
    );
  }

  const patientDob = patientProfile?.date_of_birth || appointment.patient_dob || null;
  const roleplayNhsNumber =
    patientProfile?.roleplay_nhs_number || patientRecord?.roleplay_nhs_number || null;
  const patientRoleplayName =
    patientProfile?.roleplay_name || appointment.roleplay_name || appointment.patient_full_name;
  const patientRobloxUsername =
    patientProfile?.roblox_username || appointment.roblox_username || patientRecord?.roblox_username;
  const patientDiscordUsername =
    patientProfile?.discord_username || appointment.discord_name || null;
  const patientDiscordId = patientProfile?.discord_id || appointment.discord_id || null;
  const currentAge = calculateAgeFromDob(patientDob);
  const ageAtAppointment =
    calculateAgeFromDob(patientDob, appointment.appointment_datetime) ?? appointment.patient_age;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1 p-6 pguh-appointment-page">
        <div className="pguh-appointment-page__heading">
          <div>
            <span className="pguh-eyebrow">eCAMIS appointment</span>
            <h1 className="nhsuk-heading-l mb-2">
              Appointment: {appointment.patient_full_name}
            </h1>
          </div>

          <div
            className={`pguh-appointment-status-banner${
              cancelled ? " pguh-appointment-status-banner--cancelled" : ""
            }`}
          >
            <strong>Status:</strong>{" "}
            {appointment.appointment_status || (cancelled ? "Cancelled" : "Scheduled")}
          </div>
        </div>

        <div className="pguh-appointment-page__grid">
          <section className="pguh-appointment-page__panel">
            <div className="pguh-appointment-page__panel-heading">
              <div>
                <span className="pguh-eyebrow">Appointment</span>
                <h2 className="nhsuk-heading-m">Appointment details</h2>
              </div>
            </div>

            <div className="pguh-appointment-page__form-grid">
              <div className="pguh-appointment-page__field">
                <label htmlFor="appointment-patient-name">Patient</label>
                <input
                  id="appointment-patient-name"
                  type="text"
                  className="nhsuk-input"
                  value={appointment.patient_full_name || ""}
                  disabled
                />
              </div>

              <div className="pguh-appointment-page__field">
                <label htmlFor="appointment-status">Status</label>
                <input
                  id="appointment-status"
                  type="text"
                  className="nhsuk-input"
                  value={appointment.appointment_status || (cancelled ? "Cancelled" : "Scheduled")}
                  disabled
                />
              </div>

              <div className="pguh-appointment-page__field">
                <label htmlFor="appointment-department">Department</label>
                <input
                  id="appointment-department"
                  type="text"
                  className="nhsuk-input"
                  value={editing ? form.department : appointment.department || ""}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, department: event.target.value }))
                  }
                  disabled={!editing || cancelled}
                />
              </div>

              <div className="pguh-appointment-page__field">
                <label htmlFor="appointment-clinician">Clinician</label>
                {editing ? (
                  <select
                    id="appointment-clinician"
                    className="nhsuk-select"
                    value={form.clinician || ""}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, clinician: event.target.value }))
                    }
                    disabled={cancelled}
                  >
                    <option value="">-- Select Clinician --</option>
                    {staff.map((person) => (
                      <option key={person.id} value={person.id}>
                        {formatProfileName(person)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="appointment-clinician"
                    type="text"
                    className="nhsuk-input"
                    value={
                      formatProfileName(staff.find((person) => person.id === appointment.clinician), "") ||
                      "Not assigned"
                    }
                    disabled
                  />
                )}
              </div>

              <div className="pguh-appointment-page__field">
                <label htmlFor="appointment-datetime">Date &amp; Time</label>
                <input
                  id="appointment-datetime"
                  type="datetime-local"
                  className="nhsuk-input"
                  value={
                    editing
                      ? form.appointment_datetime
                      : toDateTimeLocalValue(appointment.appointment_datetime)
                  }
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      appointment_datetime: event.target.value,
                    }))
                  }
                  disabled={!editing || cancelled}
                />
              </div>

              <div className="pguh-appointment-page__field">
                <label htmlFor="appointment-duration">Duration (minutes)</label>
                <input
                  id="appointment-duration"
                  type="number"
                  className="nhsuk-input"
                  value={editing ? form.duration_minutes : appointment.duration_minutes ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      duration_minutes: event.target.value,
                    }))
                  }
                  min="1"
                  disabled={!editing || cancelled}
                />
              </div>

              <div className="pguh-appointment-page__field pguh-appointment-page__field--wide">
                <label htmlFor="appointment-location">Location</label>
                <select
                  id="appointment-location"
                  className="nhsuk-select"
                  value={editing ? form.location : appointment.location || "Prince George University Hospital"}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, location: event.target.value }))
                  }
                  disabled={!editing || cancelled}
                >
                  <option value="Prince George University Hospital">
                    Prince George University Hospital
                  </option>
                  <option value="Duke of York Hospital">Duke of York Hospital</option>
                </select>
              </div>

              <div className="pguh-appointment-page__field pguh-appointment-page__field--wide">
                <label htmlFor="appointment-address">Address / location details</label>
                <input
                  id="appointment-address"
                  type="text"
                  className="nhsuk-input"
                  value={editing ? form.address : appointment.address || ""}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, address: event.target.value }))
                  }
                  disabled={!editing || cancelled}
                />
              </div>

              <div className="pguh-appointment-page__field pguh-appointment-page__field--wide">
                <label htmlFor="appointment-preparation">Preparation</label>
                <textarea
                  id="appointment-preparation"
                  className="nhsuk-textarea"
                  value={editing ? form.preparation : appointment.preparation || ""}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, preparation: event.target.value }))
                  }
                  disabled={!editing || cancelled}
                />
              </div>

              <div className="pguh-appointment-page__field pguh-appointment-page__field--wide">
                <label htmlFor="appointment-notes">Notes</label>
                <textarea
                  id="appointment-notes"
                  className="nhsuk-textarea"
                  value={editing ? form.notes : appointment.notes || ""}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, notes: event.target.value }))
                  }
                  disabled={!editing || cancelled}
                />
              </div>
            </div>
          </section>

          <aside className="pguh-appointment-page__panel pguh-appointment-page__patient-panel">
            <div className="pguh-appointment-page__panel-heading">
              <div>
                <span className="pguh-eyebrow">Patient</span>
                <h2 className="nhsuk-heading-m">Patient details</h2>
              </div>

              {patientProfile?.id && (
                <Link
                  className="nhsuk-button nhsuk-button--secondary pguh-appointment-page__overview-button"
                  to={`/intranet/ecamis/patient-overview/${patientProfile.id}`}
                >
                  Full patient overview
                </Link>
              )}
            </div>

            {patientLoading ? (
              <p className="nhsuk-body">Loading patient details…</p>
            ) : (
              <>
                <div className="pguh-appointment-page__nhs-number">
                  <span>Roleplay NHS number</span>
                  <strong>{formatRoleplayNhsNumber(roleplayNhsNumber)}</strong>
                </div>

                <dl className="pguh-appointment-page__patient-details">
                  <div>
                    <dt>Full name</dt>
                    <dd>{textOrFallback(appointment.patient_full_name)}</dd>
                  </div>
                  <div>
                    <dt>Roleplay name</dt>
                    <dd>{textOrFallback(patientRoleplayName)}</dd>
                  </div>
                  <div>
                    <dt>Date of birth</dt>
                    <dd>{formatDate(patientDob)}</dd>
                  </div>
                  <div>
                    <dt>Current age</dt>
                    <dd>{currentAge ?? "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt>Age at appointment</dt>
                    <dd>{ageAtAppointment ?? "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt>Pronouns</dt>
                    <dd>{textOrFallback(patientProfile?.pronouns)}</dd>
                  </div>
                  <div>
                    <dt>Roblox username</dt>
                    <dd>{patientRobloxUsername ? `@${patientRobloxUsername}` : "Not provided"}</dd>
                  </div>
                  <div>
                    <dt>Roblox user ID</dt>
                    <dd>{textOrFallback(patientProfile?.roblox_user_id, "Not recorded")}</dd>
                  </div>
                  <div>
                    <dt>Discord username</dt>
                    <dd>{textOrFallback(patientDiscordUsername)}</dd>
                  </div>
                  <div>
                    <dt>Discord user ID</dt>
                    <dd>{textOrFallback(patientDiscordId)}</dd>
                  </div>
                  <div>
                    <dt>Height</dt>
                    <dd>
                      {patientProfile?.height_cm != null
                        ? `${patientProfile.height_cm} cm`
                        : "Not provided"}
                    </dd>
                  </div>
                  <div>
                    <dt>Weight</dt>
                    <dd>
                      {patientProfile?.weight_kg != null
                        ? `${patientProfile.weight_kg} kg`
                        : "Not provided"}
                    </dd>
                  </div>
                  <div className="pguh-appointment-page__patient-detail--wide">
                    <dt>Allergies</dt>
                    <dd>{textOrFallback(patientProfile?.allergies)}</dd>
                  </div>
                  <div className="pguh-appointment-page__patient-detail--wide">
                    <dt>Accessibility needs</dt>
                    <dd>{textOrFallback(patientProfile?.accessibility_needs)}</dd>
                  </div>
                  <div className="pguh-appointment-page__patient-detail--wide">
                    <dt>Communication preferences</dt>
                    <dd>{textOrFallback(patientProfile?.communication_preferences)}</dd>
                  </div>
                  <div className="pguh-appointment-page__patient-detail--wide">
                    <dt>Other preferences</dt>
                    <dd>{textOrFallback(patientProfile?.preferences)}</dd>
                  </div>
                </dl>

                {!patientProfile && (
                  <div className="nhsuk-inset-text pguh-appointment-page__patient-warning">
                    This appointment is not currently linked to a patient portal profile. Details shown
                    here are limited to the information stored on the appointment and patient record.
                  </div>
                )}
              </>
            )}
          </aside>
        </div>

        <div className="pguh-appointment-page__actions">
          {editing ? (
            <>
              <button className="nhsuk-button" onClick={handleSave} disabled={cancelled}>
                Confirm Changes
              </button>
              <button
                className="nhsuk-button nhsuk-button--tertiary"
                onClick={handleEditToggle}
                disabled={cancelled}
              >
                Cancel Edit
              </button>
            </>
          ) : (
            <>
              <button className="nhsuk-button" onClick={handleEditToggle} disabled={cancelled}>
                Edit appointment
              </button>
              <button
                className="nhsuk-button nhsuk-button--secondary"
                onClick={handleCancel}
                disabled={cancelled}
              >
                {cancelled ? "Appointment Cancelled" : "Cancel Appointment"}
              </button>
            </>
          )}

          <button
            className="nhsuk-button nhsuk-button--tertiary"
            type="button"
            onClick={() => navigate("/intranet/ecamis/appointments")}
          >
            Back to appointments
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
